import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { PinDialog } from "@/components/PinDialog";
import { formatMoney } from "@/lib/money";
import { getDeviceId } from "@/lib/device";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, ShieldAlert } from "lucide-react";

interface RequestRow {
  id: string;
  requester_id: string;
  amount_cents: number;
  note: string | null;
  status: string;
  created_at: string;
}
interface ProfileRow {
  display_name: string;
  cashtag: string;
  avatar_url: string | null;
}

const PayRequest = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [req, setReq] = useState<RequestRow | null>(null);
  const [requester, setRequester] = useState<ProfileRow | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [hasPinSet, setHasPinSet] = useState<boolean | null>(null);
  const [pinOpen, setPinOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data: r } = await supabase
        .from("money_requests")
        .select("*")
        .eq("id", id)
        .maybeSingle();
      if (!r) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setReq(r as RequestRow);
      const { data: p } = await supabase
        .from("profiles")
        .select("display_name, cashtag, avatar_url")
        .eq("user_id", (r as RequestRow).requester_id)
        .maybeSingle();
      setRequester(p as ProfileRow | null);
      setLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    if (!user) return;
    supabase.from("wallets").select("balance_cents").eq("user_id", user.id).maybeSingle()
      .then(({ data }) => setBalance((data as any)?.balance_cents ?? 0));
    supabase.rpc("has_pin").then(({ data }) => setHasPinSet(Boolean(data)));
  }, [user]);

  // Idempotency key persists across retries of the SAME confirmation flow.
  const [idemKey, setIdemKey] = useState<string | null>(null);

  const handlePay = () => {
    if (!req) return;
    if (!user) {
      navigate(`/auth?next=${encodeURIComponent(`/r/${req.id}`)}`);
      return;
    }
    if (!hasPinSet) {
      toast.error("Set a wallet PIN in Settings first");
      return;
    }
    setIdemKey(crypto.randomUUID());
    setPinOpen(true);
  };

  const handleConfirm = async (pin: string) => {
    if (!req) return;
    const key = idemKey ?? crypto.randomUUID();
    if (!idemKey) setIdemKey(key);
    setPaying(true);
    const { data, error } = await supabase.functions.invoke("pay-request", {
      body: {
        request_id: req.id,
        pin,
        device_id: getDeviceId(),
        idempotency_key: key,
      },
    });
    setPaying(false);
    const errMsg = (error as any)?.message || (data as any)?.error;
    if (errMsg) {
      toast.error(errMsg);
      return;
    }
    setPinOpen(false);
    setIdemKey(null);
    toast.success(`Paid ${formatMoney(req.amount_cents)} 🌴`);
    navigate("/wallet", { replace: true });
  };

  if (loading || authLoading) {
    return (
      <MobileShell>
        <BrandHeader />
        <div className="flex-1 grid place-items-center text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </MobileShell>
    );
  }

  if (notFound || !req) {
    return (
      <MobileShell>
        <BrandHeader onBack={() => navigate("/")} />
        <div className="px-5 py-12 flex-1 flex flex-col items-center text-center">
          <AlertCircle className="h-10 w-10 text-muted-foreground" />
          <h2 className="mt-3 font-display text-xl font-bold">Request not found</h2>
          <p className="mt-1 text-sm text-muted-foreground">This payment link is invalid or was removed.</p>
          <Button asChild variant="hero" size="xl" className="mt-6">
            <Link to="/">Go home</Link>
          </Button>
        </div>
      </MobileShell>
    );
  }

  const isOwner = user?.id === req.requester_id;
  const isPaid = req.status === "paid";
  const isCancelled = req.status === "cancelled";
  const insufficient = user && balance !== null && balance < req.amount_cents;

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold">Payment Request</h1>
      </BrandHeader>

      <div className="px-5 py-8 flex-1 flex flex-col items-center animate-fade-in-up">
        <div className="h-16 w-16 rounded-full bg-primary/10 grid place-items-center text-primary font-display text-2xl font-bold overflow-hidden">
          {requester?.avatar_url ? (
            <img src={requester.avatar_url} alt={requester.display_name} className="h-full w-full object-cover" />
          ) : (
            requester?.display_name?.[0]?.toUpperCase() ?? "?"
          )}
        </div>
        <p className="mt-3 font-medium">{requester?.display_name ?? "Unknown"}</p>
        <p className="text-xs text-muted-foreground">${requester?.cashtag}</p>

        <p className="mt-6 text-xs uppercase tracking-wider text-muted-foreground">is requesting</p>
        <p className="font-display text-5xl font-extrabold text-primary mt-1">
          {formatMoney(req.amount_cents)}
        </p>
        {req.note && <p className="mt-2 text-sm text-muted-foreground text-center max-w-xs">"{req.note}"</p>}

        <div className="mt-8 w-full">
          {isPaid ? (
            <div className="flex flex-col items-center text-success">
              <CheckCircle2 className="h-8 w-8" />
              <p className="mt-2 font-medium">This request was already paid</p>
              <Button asChild variant="outline" className="mt-4">
                <Link to="/wallet">Back to wallet</Link>
              </Button>
            </div>
          ) : isCancelled ? (
            <div className="flex flex-col items-center text-muted-foreground">
              <AlertCircle className="h-8 w-8" />
              <p className="mt-2 font-medium">This request was cancelled</p>
            </div>
          ) : isOwner ? (
            <div className="text-center text-sm text-muted-foreground">
              <p>This is your request. Share the link to get paid.</p>
              <Button asChild variant="outline" className="mt-4">
                <Link to="/wallet">Back to wallet</Link>
              </Button>
            </div>
          ) : (
            <>
              {user && (
                <p className="text-xs text-muted-foreground text-center mb-2">
                  Your balance: {balance !== null ? formatMoney(balance) : "—"}
                </p>
              )}
              {insufficient && (
                <p className="text-xs text-destructive text-center mb-2">Not enough balance</p>
              )}
              {user && hasPinSet === false && (
                <Link
                  to="/settings"
                  className="flex items-center gap-2 rounded-xl border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs mb-3"
                >
                  <ShieldAlert className="h-4 w-4 text-destructive shrink-0" />
                  <span className="flex-1">Set a wallet PIN before paying.</span>
                  <span className="text-destructive font-medium">Set up</span>
                </Link>
              )}
              <Button
                onClick={handlePay}
                variant="hero"
                size="xl"
                className="w-full"
                disabled={paying || !!insufficient}
              >
                {paying ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : user ? (
                  `Pay ${formatMoney(req.amount_cents)}`
                ) : (
                  `Sign in to pay ${formatMoney(req.amount_cents)}`
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      <PinDialog
        open={pinOpen}
        title={`Pay ${formatMoney(req.amount_cents)}`}
        description={`To $${requester?.cashtag ?? ""}`}
        onCancel={() => { setPinOpen(false); setIdemKey(null); }}
        onSubmit={handleConfirm}
        pending={paying}
      />

      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default PayRequest;
